import {formatCurrency} from "@bounce/shared";
import {AccountSwitcherDetails} from "@bounce/shared/consumer/declarations";
import {Container} from "@bounce/shared/ui";
import NiceModal from "@ebay/nice-modal-react";
import {Button, MenuItem, TextField} from "@mui/material";
import React, {useEffect, useState} from "react";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {useGetUserQuery} from "~/api/requests";
import {useResetAccount} from "~/api/requests/dev/resetAccount";
import {useAccountId} from "~/hooks";

interface ResetAccountTabProps {
  onClose: () => void;
}

const accountLabel = (account: AccountSwitcherDetails): string => {
  const parts = [
    account.external_account_id || account.account_id,
    account.partner_code,
    account.status,
    formatCurrency(account.total_balance),
  ];
  if (account.closed) parts.push("closed");
  return parts.join(" · ");
};

export const ResetAccountTab = ({onClose}: ResetAccountTabProps) => {
  const urlAccountId = useAccountId();
  const {data: user} = useGetUserQuery({});
  const accounts = (user?.accounts_switcher as AccountSwitcherDetails[] | undefined) ?? [];

  const [accountId, setAccountId] = useState(String(urlAccountId ?? ""));
  const [balance, setBalance] = useState("");
  const {mutate: resetAccount, isPending} = useResetAccount();

  useEffect(() => {
    if (urlAccountId) {
      setAccountId(String(urlAccountId));
    }
  }, [urlAccountId]);

  const trimmedAccountId = accountId.trim();

  const handleReset = () => {
    if (!trimmedAccountId) return;

    const parsedBalance = balance.trim() === "" ? undefined : Number(balance);

    NiceModal.show(AreYouSureDialog, {
      headerTextId: "Reset Account",
      contentTextId: `Are you sure you want to reset account ${trimmedAccountId}${
        parsedBalance != null ? ` to balance ${parsedBalance}` : ""
      }? This action cannot be undone.`,
      confirmTextId: "Reset Account",
      onConfirm: () => {
        resetAccount(
          {
            accountId: trimmedAccountId,
            balance: parsedBalance,
          },
          {
            onSuccess: () => {
              setAccountId("");
              setBalance("");
              onClose();
            },
          }
        );
      },
    });
  };

  return (
    <Container sx={{gap: 2}}>
      {accounts.length > 0 ? (
        <TextField
          select
          fullWidth
          label="Account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          size="small"
        >
          {accounts.map((account) => (
            <MenuItem key={account.account_id} value={String(account.account_id)}>
              {accountLabel(account)}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          fullWidth
          label="Account ID"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          size="small"
        />
      )}

      <TextField
        fullWidth
        label="Balance in cents (optional)"
        value={balance}
        onChange={(e) => setBalance(e.target.value.replace(/[^0-9]/g, ""))}
        size="small"
        type="number"
        helperText="Leave empty to use the account's placement balance"
      />

      <Button
        variant="contained"
        color="error"
        onClick={handleReset}
        disabled={!trimmedAccountId || isPending}
        sx={{alignSelf: "flex-start"}}
      >
        {isPending ? "Resetting..." : "Reset Account"}
      </Button>
    </Container>
  );
};

export default ResetAccountTab;
